/**
 * Animazione d'ingresso dei componenti lab-* di 120''LAB.
 *
 * Riproduce le animazioni `animate-section` / `animate-item` del vecchio tema senza librerie.
 * Contratto con i componenti (piano, decisione D5):
 * - l'elemento da animare porta l'attributo `data-lab-reveal`;
 * - il ritardo a cascata si imposta con lo stile inline `--lab-index:N` (lo legge il CSS);
 * - il CSS di `assets/lab-overrides.css` (blocco "reveal") nasconde questi elementi solo
 *   quando `<html>` ha la classe `lab-js` e non siamo nell'editor del tema.
 *
 * Questo modulo:
 * - aggiunge `lab-reveal-ready` a `<html>` (il failsafe CSS mostra comunque gli elementi
 *   dopo 4 secondi se la classe manca);
 * - aggiunge `is-shown` a ogni elemento la prima volta che entra nel viewport (una sola volta);
 * - mostra subito gli elementi nell'editor del tema, con `prefers-reduced-motion` e nei
 *   browser senza IntersectionObserver;
 * - tiene sotto controllo anche gli elementi inseriti dopo il caricamento (sezioni
 *   ri-renderizzate dall'editor o dalla Section Rendering API);
 * - apre in una nuova scheda i link verso altri domini, come faceva il vecchio tema.
 *
 * Il bersaglio deve avere un box: con `display: contents` non ci sono intersezioni e
 * l'elemento resterebbe nascosto.
 */

const root = document.documentElement;
const REVEAL_SELECTOR = '[data-lab-reveal]';
const SHOWN_CLASS = 'is-shown';
const READY_CLASS = 'lab-reveal-ready';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Nell'editor del tema Horizon `<html>` porta la classe `shopify-design-mode`.
 * @returns {boolean}
 */
function isDesignMode() {
  return root.classList.contains('shopify-design-mode') || window.Shopify?.designMode === true;
}

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersection(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    entry.target.classList.add(SHOWN_CLASS);
    revealObserver?.unobserve(entry.target);
  }
}

/**
 * Root `null` (viewport): con lo scroll su `.page-wrapper` (da 990px in Horizon) la
 * geometria resta corretta. Il margine inferiore di -50px fa scattare l'animazione quando
 * l'elemento sale sopra la fascia inferiore di 50px del viewport, come nel vecchio tema.
 * @type {IntersectionObserver | null}
 */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0,
      })
    : null;

/**
 * Mostra subito oppure osserva gli elementi `[data-lab-reveal]` contenuti in `scope`
 * (il documento, una sezione, un nodo appena inserito).
 * @param {Document | Element} scope
 */
function prepareReveal(scope) {
  /** @type {Element[]} */
  const targets = Array.from(scope.querySelectorAll(REVEAL_SELECTOR));
  if (scope instanceof Element && scope.matches(REVEAL_SELECTOR)) targets.unshift(scope);

  const showNow = !revealObserver || isDesignMode() || reducedMotion.matches;

  for (const target of targets) {
    if (target.classList.contains(SHOWN_CLASS)) continue;

    if (showNow) {
      target.classList.add(SHOWN_CLASS);
    } else {
      revealObserver?.observe(target);
    }
  }
}

/**
 * Link verso altri domini: si aprono in una nuova scheda con `rel` sicuro.
 * Restano fuori i link relativi e quelli senza host (`mailto:`, `tel:`),
 * e nell'editor del tema non si tocca nulla.
 */
function openExternalLinks() {
  if (isDesignMode()) return;

  const ownHost = window.location.hostname;
  /** @type {NodeListOf<HTMLAnchorElement>} */
  const links = document.querySelectorAll('a[href^="http"]');

  links.forEach((link) => {
    if (link.hostname === ownHost) return;

    link.target = '_blank';
    link.rel = 'noreferrer noopener';
  });
}

// Il modulo viene eseguito dopo il parsing, prima di DOMContentLoaded: il failsafe CSS
// non deve scattare e gli elementi gia presenti vanno preparati subito.
root.classList.add(READY_CLASS);
prepareReveal(document);

// Elementi inseriti dopo il caricamento: sezioni ri-renderizzate dall'editor del tema
// (evento esplicito) e qualunque altro nodo aggiunto al DOM.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) prepareReveal(event.target);
});

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) prepareReveal(node);
    }
  }
}).observe(document, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openExternalLinks, { once: true });
} else {
  openExternalLinks();
}
